// Dependencies
import React, { Component } from 'react';

// Primereact imports
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';

// Multi lang
import { withTranslation } from 'react-multi-lang';

// Styling
import './interest-areas-data.scss';

// Redux
import { connect } from 'react-redux';
import ProfileService from '../../../redux/services/profile';
import { getAssociateData, setEditMode } from '../../../redux/actions/profile';

// Utils
import { INTEREST_AREAS } from '../../../common/constants';
import { formatAssociateDataToService } from '../../../common/formatters/associate';

const SECTIONS = [
    {
        key: 'physio',
        icon: 'pi pi-heart',
        titleKey: 'profile.areas.interest-areas.category-physio',
        items: [
            { prop: 'cardioRespiratory',       key: 'cardio-respiratory' },
            { prop: 'dermatofunctional',        key: 'dermatofunctional' },
            { prop: 'sportHealth',              key: 'sports-health' },
            { prop: 'intensiveCare',            key: 'intensive-care' },
            { prop: 'palliativeCare',           key: 'palliative-care' },
            { prop: 'emergencyDisaster',        key: 'emergency-disaster' },
            { prop: 'gerontology',              key: 'gerontology' },
            { prop: 'oncology',                 key: 'oncology' },
            { prop: 'pediatrics',               key: 'pediatrics' },
            { prop: 'animalHealth',             key: 'animal-health' },
            { prop: 'communityHealth',          key: 'community-health' },
            { prop: 'womensHealth',             key: 'womens-health' },
            { prop: 'schoolHealth',             key: 'school-health' },
            { prop: 'mentalHealth',             key: 'mental-health' },
            { prop: 'occupationalHealth',       key: 'occupational-health' },
            { prop: 'pelvicHealth',             key: 'pelvic-health' },
            { prop: 'publicHealth',             key: 'public-health' },
            { prop: 'musculoskeletal',          key: 'musculoskeletal' },
            { prop: 'musculoskeletalAmputees',  key: 'musculoskeletal-amputees', sub: true },
            { prop: 'musculoskeletalTMJ',       key: 'musculoskeletal-tmj',      sub: true },
            { prop: 'pain',                     key: 'pain' },
            { prop: 'healthProtection',         key: 'health-protection' },
            { prop: 'neurologicalParent',       key: 'neurological' },
            { prop: 'neurologicalVestibular',   key: 'neurological-vestibular',  sub: true },
        ]
    },
    {
        key: 'assessment',
        icon: 'pi pi-search',
        titleKey: 'profile.areas.interest-areas.category-assessment',
        items: [
            { prop: 'ptDiagnosis',   key: 'pt-diagnosis' },
            { prop: 'examAssessment', key: 'exam-assessment' },
        ]
    },
    {
        key: 'intervention',
        icon: 'pi pi-bolt',
        titleKey: 'profile.areas.interest-areas.category-intervention',
        items: [
            { prop: 'aquatic',               key: 'aquatic' },
            { prop: 'electrophysiology',     key: 'electrophysiology' },
            { prop: 'exercise',              key: 'exercise' },
            { prop: 'aiInterventions',       key: 'ai-interventions' },
            { prop: 'immobilizationDevices', key: 'immobilization-devices' },
            { prop: 'educationalMeans',      key: 'educational-means' },
            { prop: 'telePhysio',            key: 'tele-physio' },
            { prop: 'manualTherapies',       key: 'manual-therapies' },
            { prop: 'physicalTherapy',       key: 'physical-therapy' },
            { prop: 'thermalism',            key: 'thermalism' },
        ]
    },
    {
        key: 'records',
        icon: 'pi pi-file',
        titleKey: 'profile.areas.interest-areas.category-records',
        items: [
            { prop: 'clinicalRecord', key: 'clinical-record' },
        ]
    },
    {
        key: 'other',
        icon: 'pi pi-th-large',
        titleKey: 'profile.areas.interest-areas.category-other',
        items: [
            { prop: 'teaching',       key: 'teaching' },
            { prop: 'management',     key: 'management' },
            { prop: 'research',       key: 'research' },
            { prop: 'healthPromotion', key: 'health-promotion' },
            { prop: 'patientSafety',  key: 'patient-safety' },
        ]
    }
];

const buildInitialState = () => {
    const state = { readOnlyMode: true };
    SECTIONS.forEach(section => {
        section.items.forEach(item => { state[item.prop] = false; });
    });
    return state;
};

class InterestAreasDataComponent extends Component {

    constructor(props) {
        super(props);
        this._toast = React.createRef();
        this.state = buildInitialState();
    }

    componentDidMount() {
        this.loadFromProps();
    }

    componentDidUpdate(prevProps) {
        if (this.props.associate !== prevProps.associate) {
            this.loadFromProps();
        }
    }

    loadFromProps() {
        const ia = this.props.associate && this.props.associate.interestAreaData;
        if (ia) {
            const updates = {};
            SECTIONS.forEach(section => {
                section.items.forEach(item => {
                    updates[item.prop] = ia[item.prop] || false;
                });
            });
            this.setState(updates);
        }
    }

    activateEditMode() {
        this.setState({ readOnlyMode: false });
        this.props.setEditMode(true);
    }

    cancelEditMode() {
        this.loadFromProps();
        this.setState({ readOnlyMode: true });
        this.props.setEditMode(false);
    }

    toggleSection(sectionKey) {
        const section = SECTIONS.find(s => s.key === sectionKey);
        const allChecked = section.items.every(item => this.state[item.prop]);
        const updates = {};
        section.items.forEach(item => { updates[item.prop] = !allChecked; });
        this.setState(updates);
    }

    saveChanges() {
        let associate = this.props.associate;
        associate.interestAreaData = associate.interestAreaData || {};
        SECTIONS.forEach(section => {
            section.items.forEach(item => {
                associate.interestAreaData[item.prop] = this.state[item.prop] || false;
            });
        });

        ProfileService.updateAssociateData(
            formatAssociateDataToService(associate),
            this.props.t('general.update-record'),
            INTEREST_AREAS
        )
        .then(() => {
            this.setState({ readOnlyMode: true });
            this.props.setEditMode(false);
            this.props.getAssociateData(this.props.registrationRef);
        })
        .catch(() => {
            this._toast.current.show({ severity: 'error', detail: this.props.t('profile.messages.error'), life: 3000 });
        });
    }

    renderSection(section) {
        const allChecked = section.items.every(item => this.state[item.prop]);
        const { readOnlyMode } = this.state;

        return (
            <div key={section.key} className="interest-areas-section">
                <div className="interest-areas-section-header">
                    <i className={section.icon}></i>
                    <span>{this.props.t(section.titleKey)}</span>
                </div>
                <div className="interest-areas-column-header">
                    <span>{this.props.t('profile.areas.interest-areas.interest-header')}</span>
                </div>
                <div
                    className={`interest-areas-select-all-row${!readOnlyMode ? ' clickable' : ''}`}
                    onClick={!readOnlyMode ? () => this.toggleSection(section.key) : undefined}
                >
                    <label>{allChecked
                        ? this.props.t('profile.areas.interest-areas.deselect-all')
                        : this.props.t('profile.areas.interest-areas.select-all')
                    }</label>
                    <Checkbox
                        checked={allChecked}
                        disabled={readOnlyMode}
                        onChange={() => this.toggleSection(section.key)}
                    />
                </div>
                {section.items.map(item => (
                    <div key={item.prop} className={`interest-areas-row${item.sub ? ' sub-item' : ''}`}>
                        <span className="item-label">
                            {item.sub && <span className="sub-bullet">•</span>}
                            {this.props.t(`profile.areas.interest-areas.${item.key}`)}
                        </span>
                        <Checkbox
                            inputId={item.prop}
                            checked={this.state[item.prop]}
                            disabled={readOnlyMode}
                            onChange={(e) => this.setState({ [item.prop]: e.checked })}
                        />
                    </div>
                ))}
            </div>
        );
    }

    render() {
        return (
            this.props.associate !== null ?
                <div className='grid mgb-80'>
                    <Toast ref={this._toast} />
                    <div className='col-12'>
                        <div className="card p-fluid mgt-20">
                            <div className='grid'>
                                <div className='col-12 t-align-right'>
                                    {
                                        this.state.readOnlyMode === true ?
                                            <Button icon="pi pi-pencil" className="p-button-rounded mr-2 mb-2" disabled={!this.props.registrationApproved} onClick={() => this.activateEditMode()} />
                                            :
                                            <>
                                                <Button icon="pi pi-check" className="p-button-rounded p-button-success mr-2 mb-2" onClick={() => this.saveChanges()} />
                                                <Button icon="pi pi-ban" className="p-button-rounded p-button-danger mr-2 mb-2" onClick={() => this.cancelEditMode()} />
                                            </>
                                    }
                                </div>
                                <div className='col-12 t-align-left'>
                                    <p className="interest-areas-subtitle">{this.props.t('profile.areas.interest-areas.subtitle')}</p>
                                    {SECTIONS.map(section => this.renderSection(section))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                : null
        );
    }
}

const mapStateToProps = (state) => {
    return {
        associate: state.profile.associate,
        registrationRef: state.authentication.registrationRef,
        registrationApproved: state.authentication.registrationApproved,
        language: state.profile.language
    }
};

const mapDispatchToProps = (dispatch) => {
    return {
        setEditMode: (editMode) => { dispatch(setEditMode(editMode)) },
        getAssociateData: (registrationRef) => { dispatch(getAssociateData(registrationRef)) }
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation(InterestAreasDataComponent));
